import { z } from 'zod';
import { ulidSchema } from './id';

export const STICKER_NAME_MAX_LENGTH = 255;
export const STICKER_IMAGE_URL_MAX_LENGTH = 2048;
export const STICKER_UPLOAD_MAX_BYTES = 1024 * 1024;
export const STICKER_UPLOAD_CONTENT_TYPES = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
] as const;

export const stickerSurfaceSchema = z.enum(['front', 'back']);
export type StickerSurface = z.infer<typeof stickerSurfaceSchema>;

/**
 * "At most `scale` decimal places", for a `numeric(_, scale)` column.
 *
 * ---------------------------------------------------------------------------
 * ⚠⚠ THIS REFINE SHIPPED AS `Number.isInteger(value * factor)` AND THAT WAS A
 * DEFECT, MEASURED RATHER THAN SUSPECTED
 * ---------------------------------------------------------------------------
 * `value * factor` is IEEE-754 double multiplication, and for a great many
 * perfectly legal four-decimal values it lands one unit in the last place
 * below the integer it should be: `12.3456 * 10000` is
 * **`123455.99999999999`**, so `Number.isInteger` says `false` and the schema
 * refuses `12.3456` — a position `numeric(8,4)` stores exactly.
 *
 * **Measured EXHAUSTIVELY over the column's whole domain, not sampled:** of the
 * **100,000,000** four-decimal values in `[0, 9999.9999]` — every value
 * `numeric(8,4)` can hold on the positive side — the shipped predicate rejected
 * **12,611,704, or 12.61%**. That is not an edge case; it is one placement in
 * eight. (An earlier note in this session said 12.7% from a `[0, 20)` sample of
 * 200,000; the full-domain figure supersedes it.)
 *
 * It failed in BOTH directions and the second one is the expensive one:
 *   · on the way IN, `POST /cards/:id/stickers` answered **422** to an
 *     ordinary drag that happened to land on one of those coordinates;
 *   · on the way OUT, `placedStickerSchema` serialises `GET /cards/:id` and —
 *     since `FE-58.1` — `GET /boards/:id/contents`. A row carrying such a
 *     value (written by an import, a copy, a template, or by anything that is
 *     not this API) is a `ResponseSerializationError`, which is a **500 for
 *     the WHOLE BOARD FACE**: every card, every list, on a board where one
 *     person placed one sticker. It was found exactly that way, by an
 *     integration fixture that inserted `position_x = 12.3456`.
 *
 * The replacement asks the question the message has always claimed to ask —
 * *is this value the same as itself rounded to `scale` places?* — with a
 * tolerance far above double representation error (~1e-8 at this magnitude)
 * and far below the smallest real excess (a fifth decimal place contributes
 * `0.1` after scaling, i.e. 100,000× the tolerance). **Both halves are measured
 * exhaustively over the whole domain rather than sampled, and the classification
 * of a "genuine five-decimal value" is integer arithmetic (`i % 10 !== 0`),
 * never the float predicate under test — classifying with the broken predicate
 * is how a first attempt at this measurement reported a false 1.5% leak:**
 *
 *   · 100,000,000 legal four-decimal values in `[0, 9999.9999]` — **0
 *     rejected**; the largest `|v·10⁴ − round(v·10⁴)|` anywhere in the domain
 *     is `1.49e-8`, at `8192.0004`, which is **67× under** the tolerance;
 *   · 900,000,000 genuine five-decimal values in `[0, 9999.99999]` — **0
 *     wrongly accepted**; the smallest excess anywhere is `0.0999999940`,
 *     which is **100,000× over** it;
 *   · 1,000,000 legal two-decimal rotations in `[0, 9999.99]` (`decimal(2)`'s
 *     own domain) — **0 rejected**, worst error `1.16e-10`.
 *
 * The negative half is the same numbers: the predicate is `Math.abs`, and
 * IEEE-754 magnitude is sign-symmetric.
 */
const decimal = (scale: number) => {
  const factor = 10 ** scale;
  return z
    .number()
    .finite()
    .refine((value) => Math.abs(value * factor - Math.round(value * factor)) < 1e-6, {
      message: `Expected at most ${scale} decimal places`,
    });
};

export const stickerPositionSchema = decimal(4).min(-9999.9999).max(9999.9999);
export const stickerRotationSchema = decimal(2).min(-9999.99).max(9999.99);
export const stickerZIndexSchema = z.number().int().min(-2147483648).max(2147483647);

/**
 * The pixel box one placed sticker occupies ON A CARD FRONT.
 *
 * `CardTile` draws each front sticker as `className="absolute size-8
 * object-contain"`, and Tailwind's `size-8` is `2rem` = **32px** square.
 *
 * It lives in this module for the same reason `STICKER_UPLOAD_MAX_BYTES` does:
 * it is a number a placement has to be legal against *before* it goes on the
 * wire, and `clampFrontStickerPlacement` below is the only thing that makes it
 * legal. A second copy of the number inside a component would drift on the
 * first restyle, and the drift would be **invisible** — a sticker clipped away
 * by `CardTile`'s `overflow-hidden`, placed by a member who then cannot see it,
 * with nothing red anywhere.
 *
 * ⚠ If `CardTile`'s `size-8` ever changes, this constant changes with it. The
 * two are one decision written twice, and this is the copy the wire sees.
 */
export const STICKER_FRONT_RENDER_PX = 32;

export interface ClampFrontStickerPlacementInput {
  /** The pointer/keyboard coordinate, in `CardTile`'s own pixels from its origin. */
  positionX: number;
  positionY: number;
  /** The card tile's measured box — `getBoundingClientRect()`'s `width`/`height`. */
  tileWidth: number;
  tileHeight: number;
}

/** `v` rounded to the exact four-decimal lattice `numeric(8,4)` stores. */
const round4 = (value: number): number => Math.round(value * 10000) / 10000;

/** `v` rounded DOWN to that same lattice — used on a bound, never on a value. */
const floor4 = (value: number): number => Math.floor(value * 10000) / 10000;

/**
 * Bound a front-face sticker placement to the card tile it was dropped on, and
 * to what `numeric(8,4)` can actually store.
 *
 * ---------------------------------------------------------------------------
 * ⚠ THIS IS AN INPUT-SIDE ACT AND NEVER AN OUTPUT-SIDE TRANSFORM
 * ---------------------------------------------------------------------------
 * `CardTile`'s own docstring forbids the other reading in as many words —
 * *"`left`/`top` are the wire's own pixels from THIS box's origin. No scale
 * factor and no front-specific clamp … a client-side transform of the
 * coordinates would make the front and the back disagree about a placement."*
 * That stands. This function runs **once, before the POST**, so the value that
 * reaches the wire is already legal; every reader afterwards — `CardTile`,
 * `CardStickerSection`, an export, another member's browser — keeps rendering
 * `sticker.positionX` verbatim. `FR-CARD-032` AC4's *"another member opening
 * the board sees it in the same position"* is untouched: there is still exactly
 * one number, and it is this one.
 *
 * **Three things are bounded, for three different reasons.**
 *
 * 1. **Containment**, because `CardTile`'s root is `overflow-hidden`. A
 *    coordinate near the right or bottom edge draws a 32px image that is
 *    silently clipped — a sticker the member placed and cannot see. So
 *    `0 ≤ x ≤ tileWidth − 32` and `0 ≤ y ≤ tileHeight − 32`. The floor at 0 is
 *    not decoration: `stickerPositionSchema` permits −9999.9999, so a negative
 *    is a legal wire value that renders entirely off the tile.
 *
 * 2. **Four decimal places, mandatorily**, because a drop coordinate is
 *    `clientX − rect.left` and that is fractional under device-pixel-ratio and
 *    browser zoom. `stickerPositionSchema` is `decimal(4)`, so an unrounded
 *    coordinate is a **422 on an ordinary drag**. Rounding belongs here rather
 *    than at a call site precisely because there are several call sites and one
 *    of them would forget.
 *
 * 3. **The column's own ceiling**, `±9999.9999`. A caller that measures a box
 *    from a detached or mis-scaled node can hand in a `tileWidth` far past what
 *    `numeric(8,4)` holds; the bound is capped so the result is wire-legal no
 *    matter what the box says.
 *
 * **Why the bound is FLOORED to the lattice and the value is ROUNDED to it.**
 * `Math.min(round4(v), maxX)` is only exactly-4dp if `maxX` is too — and `maxX`
 * comes from `tileWidth − 32`, a fractional rect. So the bound goes through
 * `floor4` (down, never up, so containment can never be widened by rounding)
 * and the value through `round4`; the minimum of two exact-lattice numbers is
 * itself on the lattice. **Verified rather than assumed**, because the
 * `decimal(4)` docstring above measures `Math.round` and says nothing about
 * `Math.floor`: 8,000,000 values — `floor4`/`round4` of fractional widths swept
 * across 100–1200px, plus the `9999.9999` ceiling and 0 — produced **0**
 * failures of the refine, worst error `9.31e-10` at `512.0025`, which is
 * **1,073× under** the 1e-6 tolerance.
 *
 * **A non-finite input yields 0 rather than a throw.** The whole reason this
 * function exists is the guarantee *"the number it returns is one
 * `stickerPositionSchema` accepts"*, and a function that throws cannot make
 * that guarantee — `Math.max(0, Math.min(NaN, max))` is `NaN`, which is a 422.
 * A non-finite `tileWidth`/`tileHeight` collapses that axis' bound to 0 for the
 * same reason. Both are unreachable through a real pointer, and both are
 * pinned by tests, because a `NaN` that reaches the wire is the shape of every
 * defect this module already records.
 *
 * ---------------------------------------------------------------------------
 * What it deliberately does NOT do
 * ---------------------------------------------------------------------------
 * **It does not exclude the title's rectangle.** `FR-CARD-032` says stickers
 * are *"positioned freely"*, and `FR-EXP-027`'s own edge case is that *"the
 * card title layer stays above them"* — which `CardTile` already satisfies by
 * DOM order, at `z-index: auto`, with the title emitted after the sticker
 * layer. So a coordinate inside the title box yields a partially hidden
 * **sticker**, never a hidden title, and silently relocating a coordinate a
 * member deliberately chose would be the worse failure. No `z-index` and no
 * opacity ceiling is applied here either: against an arbitrary uploaded image
 * no opacity can guarantee the product's 4.5:1 floor — the effective backdrop
 * is `α·S + (1−α)·G` for an unknown `S` — so the threshold is held by the
 * stacking, by construction, and not by a number invented here.
 *
 * **It enforces no count cap.** No requirement in the corpus states one;
 * `FR-BOARD-035` enumerates nine per-board caps and stickers are not among
 * them. Inventing one is out of bounds.
 */
export function clampFrontStickerPlacement({
  positionX,
  positionY,
  tileWidth,
  tileHeight,
}: ClampFrontStickerPlacementInput): { positionX: number; positionY: number } {
  const bound = (extent: number): number => {
    if (!Number.isFinite(extent)) {
      return 0;
    }

    return floor4(Math.min(Math.max(0, extent - STICKER_FRONT_RENDER_PX), 9999.9999));
  };

  const place = (value: number, max: number): number => {
    if (!Number.isFinite(value)) {
      return 0;
    }

    return Math.min(Math.max(round4(value), 0), max);
  };

  return {
    positionX: place(positionX, bound(tileWidth)),
    positionY: place(positionY, bound(tileHeight)),
  };
}

export const stickerLibraryRowSchema = z.object({
  id: ulidSchema,
  name: z.string().max(STICKER_NAME_MAX_LENGTH).nullable(),
  isCustom: z.boolean(),
  renderUrl: z.string().min(1).max(STICKER_IMAGE_URL_MAX_LENGTH),
  createdAt: z.string().min(1),
});

export type StickerLibraryRow = z.infer<typeof stickerLibraryRowSchema>;

export const stickerLibrarySchema = z.array(stickerLibraryRowSchema);
export type StickerLibrary = z.infer<typeof stickerLibrarySchema>;

/**
 * One sticker **placed** on a card — a `card_stickers` row joined to the
 * `stickers` library row it points at.
 *
 * ---------------------------------------------------------------------------
 * ⚠ THIS ELEMENT NOW SERIALISES TWO SURFACES, NOT ONE, AND THE SHARING IS THE
 * POINT RATHER THAN AN ECONOMY
 * ---------------------------------------------------------------------------
 * Through B58 it reached the wire only through `placedStickerListSchema` on
 * `cardDetailSchema` — the card back. The Card-Front Projection contract chunk
 * puts the **same element** on `boardFaceCardSchema.stickers`
 * (`./board-contents`), so the card front and the card back cannot disagree
 * about a placement. That agreement is `FR-CARD-032` AC4's observable —
 * *"another member opening the board sees it in the same position"* — and one
 * shared element makes it a property of the type rather than of two mappers
 * staying in step.
 *
 * ⚠ **The board face carries `surface = 'front'` rows only; the card back
 * carries both.** `ck_card_stickers_surface CHECK (surface IN ('front','back'))`
 * makes that a closed two-valued choice rather than an open string test. The
 * `surface` field below therefore rides the board-face array too and is always
 * `'front'` there — redundant on that surface by construction, and kept
 * because narrowing the element for one consumer is exactly what would let the
 * two surfaces drift.
 *
 * ⚠ **`renderUrl` on the nested library row has ONE producer**
 * (`stickerRenderUrl` in `apps/server/src/routes/stickers.ts`). A second
 * builder anywhere else drifts on the first rename and the drift is invisible
 * — `cardPath`'s recorded trap. Any new reader of this shape resolves the URL
 * through that function, never by concatenating a bucket and a key of its own.
 */
export const placedStickerSchema = z.object({
  id: ulidSchema,
  cardId: ulidSchema,
  sticker: stickerLibraryRowSchema,
  surface: stickerSurfaceSchema,
  positionX: stickerPositionSchema,
  positionY: stickerPositionSchema,
  rotation: stickerRotationSchema,
  zIndex: stickerZIndexSchema,
  createdAt: z.string().min(1),
});

export type PlacedSticker = z.infer<typeof placedStickerSchema>;

export const placedStickerListSchema = z.array(placedStickerSchema);
export type PlacedStickerList = z.infer<typeof placedStickerListSchema>;

export const stickerPresignBodySchema = z.strictObject({
  fileName: z.string().min(1).max(STICKER_NAME_MAX_LENGTH),
  sizeBytes: z.number().int().positive().max(STICKER_UPLOAD_MAX_BYTES),
  contentType: z.enum(STICKER_UPLOAD_CONTENT_TYPES),
});

export type StickerPresignBody = z.infer<typeof stickerPresignBodySchema>;

export const stickerPresignSchema = z.object({
  storageKey: z.string().min(1).max(STICKER_IMAGE_URL_MAX_LENGTH),
  url: z.string().min(1),
  headers: z.record(z.string(), z.string()),
  expiresInSeconds: z.number().int().positive(),
});

export type StickerPresign = z.infer<typeof stickerPresignSchema>;

export const stickerConfirmBodySchema = z.strictObject({
  storageKey: z.string().min(1).max(STICKER_IMAGE_URL_MAX_LENGTH),
  name: z.string().trim().min(1).max(STICKER_NAME_MAX_LENGTH).nullable(),
});

export type StickerConfirmBody = z.infer<typeof stickerConfirmBodySchema>;

export const stickerPlaceBodySchema = z.strictObject({
  stickerId: ulidSchema,
  surface: stickerSurfaceSchema.default('front'),
  positionX: stickerPositionSchema,
  positionY: stickerPositionSchema,
  rotation: stickerRotationSchema.default(0),
  zIndex: stickerZIndexSchema.default(0),
});

export type StickerPlaceBody = z.infer<typeof stickerPlaceBodySchema>;

export const stickerUpdateBodySchema = z
  .strictObject({
    surface: stickerSurfaceSchema.optional(),
    positionX: stickerPositionSchema.optional(),
    positionY: stickerPositionSchema.optional(),
    rotation: stickerRotationSchema.optional(),
    zIndex: stickerZIndexSchema.optional(),
  })
  .refine((body) => Object.keys(body).length > 0, {
    message: 'At least one sticker field must be provided',
  });

export type StickerUpdateBody = z.infer<typeof stickerUpdateBodySchema>;

export const stickerRemoveBodySchema = z.strictObject({});
export type StickerRemoveBody = z.infer<typeof stickerRemoveBodySchema>;
